/**
 * FunctionPickerPanel
 *
 * Single-pane function picker hosted inside the input rail's "Expressions"
 * tab body. Mirrors the UX of {@link TokenSourceTree}: collapsible groups,
 * click-to-insert, and HTML5 drag-and-drop. Each function row exposes its
 * signature/description through a hover tooltip rather than a dedicated
 * detail pane.
 *
 * The function reference data is the same `EXPRESSION_FUNCTIONS` set used
 * by `ExpressionBuilder` and `ExpressionAutocomplete`, and the type-icon
 * convention (T / </> / calendar) mirrors `ExpressionAutocomplete` so the
 * two surfaces stay visually consistent.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useHoverIntentTooltip } from '../shared/useHoverIntentTooltip';
import { createDragImage, resolveTokenColor } from '../shared/dragImage';
import {
  Badge,
  Input,
  Text,
  Tooltip,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import {
  CalendarRegular,
  ChevronRightRegular,
  CodeFilled,
  CopyRegular,
  SearchRegular,
  TextTFilled,
} from '@fluentui/react-icons';
import {
  JAVASCRIPT_EXPRESSION_FUNCTIONS,
  searchFunctions,
  type ExpressionFunctionCategory,
  type ExpressionFunctionInfo,
} from '@/data/expressionFunctions';
import { RailHintFooter } from '../shared/RailHintFooter';
import { useFunctionPickerStyles } from './FunctionPickerPanel.styles';

export interface FunctionPickerPanelProps {
  /** Optional callback fired when a function template is inserted. */
  onInsert?: (template: string) => void;
  /** Optional className passthrough so the parent can size it. */
  className?: string;
}

type FunctionGroupKey = 'workflow' | 'global';

interface FunctionGroup {
  key: FunctionGroupKey;
  label: string;
  functions: ExpressionFunctionInfo[];
}

const WORKFLOW_CATEGORIES: ReadonlySet<ExpressionFunctionCategory> = new Set([
  'workflow',
  'reference',
]);

const GROUP_LABELS: Record<FunctionGroupKey, string> = {
  workflow: 'Workflow',
  global: 'Global functions',
};

const buildTemplate = (info: ExpressionFunctionInfo): string => {
  if (info.parameters && info.parameters.length > 0) {
    const args = info.parameters.map((p) => p.name).join(', ');
    return `${info.name}(${args})`;
  }
  return info.signature;
};

// Wait this many ms after hovering a row before showing the tooltip. Long
// enough that quickly mousing across rows toward the group header doesn't
// trigger the tooltip; short enough that a deliberate dwell still gets
// the affordance.
const TOOLTIP_HOVER_DELAY_MS = 600;

const renderTypeIcon = (
  info: ExpressionFunctionInfo,
  className: string,
): ReactNode => {
  const type = info.returnType.toLowerCase();
  if (type === 'string') return <TextTFilled className={className} />;
  if (type === 'date' || type === 'datetime')
    return <CalendarRegular className={className} />;
  return <CodeFilled className={className} />;
};

export const FunctionPickerPanel = ({
  onInsert,
  className,
}: FunctionPickerPanelProps) => {
  const styles = useFunctionPickerStyles();
  const [search, setSearch] = useState('');
  const [selectedName, setSelectedName] = useState<string | null>(
    JAVASCRIPT_EXPRESSION_FUNCTIONS[0]?.name ?? null,
  );
  const [draggingName, setDraggingName] = useState<string | null>(null);
  // Controlled tooltip with hover-intent delay. Shared with the dynamic-
  // content side (WorkflowInputPanel) — see {@link useHoverIntentTooltip}
  // for the rationale (Fluent's hover machinery doesn't fire mouseleave
  // during HTML5 drag, so we control visibility explicitly).
  const {
    openKey: openTooltipName,
    scheduleShow: scheduleTooltipShow,
    close: closeTooltip,
    setOpenKey: setOpenTooltipName,
  } = useHoverIntentTooltip(TOOLTIP_HOVER_DELAY_MS);
  const [collapsedGroups, setCollapsedGroups] = useState<
    Set<FunctionGroupKey>
  >(new Set());
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const groups = useMemo<FunctionGroup[]>(() => {
    const source = search ? searchFunctions(search) : JAVASCRIPT_EXPRESSION_FUNCTIONS;
    const workflow: ExpressionFunctionInfo[] = [];
    const global: ExpressionFunctionInfo[] = [];
    for (const fn of source) {
      if (WORKFLOW_CATEGORIES.has(fn.category)) workflow.push(fn);
      else global.push(fn);
    }
    return [
      { key: 'workflow', label: GROUP_LABELS.workflow, functions: workflow },
      { key: 'global', label: GROUP_LABELS.global, functions: global },
    ];
  }, [search]);

  // Visible-only flat list. Used for keyboard navigation (ArrowUp/Down)
  // and as the listbox's `aria-activedescendant` source — we don't want
  // ↑/↓ to move into a collapsed group.
  const flatList = useMemo<ExpressionFunctionInfo[]>(
    () =>
      groups
        .filter((g) => !collapsedGroups.has(g.key))
        .flatMap((g) => g.functions),
    [groups, collapsedGroups],
  );

  // Total functions matching the search, regardless of collapsed state. Used
  // to decide whether to show the "no results" empty state — collapsing all
  // groups should NOT pretend the search returned nothing.
  const totalMatchCount = useMemo(
    () => groups.reduce((sum, g) => sum + g.functions.length, 0),
    [groups],
  );

  // Keep selection valid as the search filter narrows the visible list.
  useEffect(() => {
    if (flatList.length === 0) {
      setSelectedName(null);
      return;
    }
    if (!selectedName || !flatList.some((f) => f.name === selectedName)) {
      setSelectedName(flatList[0]!.name);
    }
  }, [flatList, selectedName]);

  const handleInsert = useCallback(
    (info: ExpressionFunctionInfo | null) => {
      if (!info) return;
      onInsert?.(buildTemplate(info));
    },
    [onInsert],
  );

  const focusItem = useCallback((name: string) => {
    itemRefs.current[name]?.focus();
  }, []);

  const toggleGroup = useCallback((key: FunctionGroupKey) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleItemKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>, info: ExpressionFunctionInfo) => {
      // Strict-model UX (mirrors the dynamic-content side): Enter/Space on
      // the row body no longer inserts. Functions are leaf-only, so the row
      // body is selection-only — keyboard users Tab to the dedicated insert
      // button to commit. Arrow keys still move selection.
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const idx = flatList.findIndex((f) => f.name === info.name);
        const next = flatList[Math.min(flatList.length - 1, idx + 1)];
        if (next) {
          setSelectedName(next.name);
          focusItem(next.name);
        }
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        const idx = flatList.findIndex((f) => f.name === info.name);
        const prev = flatList[Math.max(0, idx - 1)];
        if (prev) {
          setSelectedName(prev.name);
          focusItem(prev.name);
        }
      }
    },
    [flatList, focusItem],
  );

  const handleDragStart = useCallback(
    (event: DragEvent<HTMLDivElement>, info: ExpressionFunctionInfo) => {
      const template = buildTemplate(info);
      setDraggingName(info.name);

      // Plain text is the only payload TokenizedInput.handleDrop falls back
      // to for non-token drags — putting the template here lets users drop
      // a function directly into a parameter field. The custom MIME marks
      // the payload as an *expression template* so the drop target can
      // wrap it in ${...} when dropped outside an existing expression.
      event.dataTransfer.setData('text/plain', template);
      event.dataTransfer.setData('application/x-otto-expression', template);
      event.dataTransfer.effectAllowed = 'copy';

      // Build the custom drag chip via the shared helper. The chip lives
      // in <body> (outside the FluentProvider scope), so we resolve the
      // Fluent teal token against the source row (which IS inside the
      // provider) before handing the literal color value to the helper.
      // The font-family is read off the source row too, falling back to a
      // platform monospace stack if the row's computed font isn't already
      // monospace.
      const sourceEl = event.currentTarget as HTMLElement;
      const computed = window.getComputedStyle(sourceEl);
      const fontFamily = computed.fontFamily.includes('monospace')
        ? computed.fontFamily
        : 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
      const resolvedTeal = resolveTokenColor(sourceEl, tokens.colorPaletteTealForeground2);
      const backgroundColor = resolvedTeal || '#0f6f6f';
      const resolvedOnBrand = resolveTokenColor(sourceEl, tokens.colorNeutralForegroundOnBrand);
      const color = resolvedOnBrand || '#ffffff';
      createDragImage(event.dataTransfer, {
        text: template,
        backgroundColor,
        color,
        fontFamily,
        nowrap: true,
      });
    },
    [],
  );

  const handleDragEnd = useCallback(() => {
    setDraggingName(null);
  }, []);

  const renderItem = (info: ExpressionFunctionInfo) => {
    const isSelected = info.name === selectedName;
    const isDragging = info.name === draggingName;
    const insertButtonLabel = `Insert ${info.name} template`;
    const tooltipContent = (
      <div className={styles.tooltipBody}>
        <div className={styles.tooltipHeader}>
          <span className={styles.tooltipName}>{info.name}</span>
          <Badge appearance="tint" color="informative" size="small">
            {info.returnType}
          </Badge>
        </div>
        <code className={styles.tooltipSignature}>{info.signature}</code>
        <Text className={styles.tooltipDescription}>{info.description}</Text>
      </div>
    );
    // Tooltip is *controlled* (not via Fluent's hover machinery) so we can
    // force-close it the instant a drag starts. HTML5 drag captures the
    // pointer and suppresses the synthetic mouseleave that Fluent normally
    // uses to dismiss, which left the tooltip floating exactly over the
    // parameter field the user was trying to drop on. Hover state is
    // tracked locally and threaded into `visible`. Positioning is
    // `above-start` so the tooltip never overlaps the right-side drop
    // target; combined with `pointer-events: none` on the tooltip surface
    // and a 600ms hover-intent delay, it can't block the group header
    // above the row either.
    const isTooltipOpen = openTooltipName === info.name && !draggingName;
    return (
      <Tooltip
        key={info.name}
        content={tooltipContent}
        relationship="description"
        positioning="above-start"
        visible={isTooltipOpen}
        onVisibleChange={(_, data) => {
          // Fluent's internal hover machinery still fires this — honour
          // close events (e.g., escape, focus moves) but ignore "open"
          // events so our hover-intent delay is the single source of truth
          // for showing the tooltip.
          if (!data.visible && openTooltipName === info.name) {
            setOpenTooltipName(null);
          }
        }}
      >
        {/* Row container is a div, not a button, so we can nest a real
            <button> for "Insert" inside. role="option" preserves the
            listbox semantics; tabIndex implements the roving-tabindex
            pattern. The row itself only selects + drags — it never inserts
            on click or Enter (matches the dynamic-content side's strict
            model). */}
        <div
          ref={(el) => {
            itemRefs.current[info.name] = el;
          }}
          role="option"
          aria-selected={isSelected}
          aria-label={`${info.name} — ${info.signature}. Drag onto a field to insert, or use the insert button.`}
          tabIndex={isSelected ? 0 : -1}
          draggable
          onDragStart={(e) => {
            // Wipe hover/tooltip state up-front so the tooltip can't survive
            // the pointer capture that HTML5 drag installs.
            closeTooltip();
            handleDragStart(e, info);
          }}
          onDragEnd={handleDragEnd}
          className={mergeClasses(
            styles.item,
            isSelected && styles.itemSelected,
            isDragging && styles.itemDragging,
          )}
          onClick={() => {
            // Cancel any pending tooltip show — clicking is decisive intent
            // and tooltips popping over selected rows is noise.
            closeTooltip();
            setSelectedName(info.name);
          }}
          onMouseEnter={() => scheduleTooltipShow(info.name)}
          onMouseLeave={closeTooltip}
          onFocus={() => setSelectedName(info.name)}
          onKeyDown={(e) => handleItemKeyDown(e, info)}
        >
          {renderTypeIcon(info, styles.itemTypeIcon)}
          <div className={styles.itemInfo}>
            <span className={styles.itemName}>{info.name}</span>
            <span className={styles.itemSignature}>{info.signature}</span>
          </div>
          <button
            type="button"
            data-insert-button
            className={styles.insertButton}
            onClick={(e) => {
              e.stopPropagation();
              handleInsert(info);
            }}
            onKeyDown={(e) => {
              // Stop the row's keyboard handler from also firing when the
              // user activates the insert button.
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
              }
            }}
            // Drag should originate on the row, not the button — the button
            // is a small icon target, dragging off it feels accidental.
            draggable={false}
            onDragStart={(e) => e.preventDefault()}
            aria-label={insertButtonLabel}
            title={insertButtonLabel}
          >
            <CopyRegular aria-hidden="true" />
          </button>
        </div>
      </Tooltip>
    );
  };

  const renderGroup = (group: FunctionGroup): ReactNode => {
    if (group.functions.length === 0) return null;
    const isCollapsed = collapsedGroups.has(group.key);
    const headerId = `function-group-${group.key}`;
    return (
      <div key={group.key} className={styles.group}>
        <button
          id={headerId}
          type="button"
          aria-expanded={!isCollapsed}
          aria-controls={`function-group-list-${group.key}`}
          onClick={() => toggleGroup(group.key)}
          className={styles.groupHeader}
        >
          <span className={styles.groupLabel}>{group.label}</span>
          <Badge
            className={styles.groupBadge}
            appearance="tint"
            size="small"
          >
            {group.functions.length}
          </Badge>
          <ChevronRightRegular
            aria-hidden="true"
            className={mergeClasses(
              styles.groupChevron,
              !isCollapsed && styles.groupChevronExpanded,
            )}
          />
        </button>
        {!isCollapsed && (
          <div
            id={`function-group-list-${group.key}`}
            role="group"
            aria-label={group.label}
            aria-labelledby={headerId}
            className={styles.itemList}
          >
            {group.functions.map(renderItem)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={mergeClasses(styles.root, className)}>
      <div className={styles.searchRow}>
        <Input
          className={styles.searchInput}
          contentBefore={<SearchRegular aria-hidden="true" />}
          placeholder="Search functions"
          value={search}
          onChange={(_, data) => setSearch(data.value)}
          size="small"
          aria-label="Search functions"
        />
      </div>
      <div
        role="listbox"
        aria-label="Functions"
        aria-activedescendant={selectedName ?? undefined}
        className={styles.list}
      >
        {totalMatchCount === 0 ? (
          <div className={styles.emptyState}>
            No functions match your search.
          </div>
        ) : (
          groups.map(renderGroup)
        )}
      </div>
      <RailHintFooter>Drag functions to parameter fields</RailHintFooter>
    </div>
  );
};
